import { useEffect, useLayoutEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { SettingsNavButton } from "@/components/ui/settings-nav-button"
import { ROUTES } from "@/routes/path"
import DeveloperSettings from "./settings/developer"
import BillingSettings from "./settings/billing"
import CreditsSettings from "./settings/credits/list"
import GeneralSettings from "./settings/general"
import TeamMembersList from "./settings/team/list"

export default function Settings() {
  const location = useLocation()
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)

  // Get current tab from URL path
  const getCurrentTab = () => {
    const path = location.pathname
    if (path.includes("/team")) return "team"
    if (path.includes("/credits")) return "credits"
    if (path.includes("/billing")) return "billing"
    if (path.includes("/developer")) return "developer"
    return "general"
  }

  // Handle tab change
  const handleTabChange = (value: string) => {
    switch (value) {
      case "general":
        navigate(ROUTES.DASHBOARD.SETTINGS.GENERAL)
        break
      case "team":
        navigate(ROUTES.DASHBOARD.SETTINGS.TEAM)
        break
      case "credits":
        navigate(ROUTES.DASHBOARD.SETTINGS.CREDITS)
        break
      case "billing":
        navigate(ROUTES.DASHBOARD.SETTINGS.BILLING)
        break
      case "developer":
        navigate(ROUTES.DASHBOARD.SETTINGS.DEVELOPER)
        break
      default:
        navigate(ROUTES.DASHBOARD.SETTINGS.GENERAL)
    }
  }

  // Redirect to general if on base settings path
  useEffect(() => {
    if (location.pathname === ROUTES.DASHBOARD.SETTINGS.INDEX) {
      navigate(ROUTES.DASHBOARD.SETTINGS.GENERAL, { replace: true })
    }
  }, [location.pathname, navigate])

  const scrollToTop = () => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Find the main scrollable container (from layout.tsx)
      const mainElement = document.querySelector("main.overflow-auto") as HTMLElement
      if (mainElement) {
        mainElement.scrollTo({ top: 0, left: 0, behavior: "auto" })
        mainElement.scrollTop = 0
      }
      // Also try window scroll as fallback
      window.scrollTo({ top: 0, left: 0, behavior: "auto" })
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    })
  }

  // Scroll to top on mount
  useLayoutEffect(() => {
    scrollToTop()
  }, [])

  // Scroll to top when route changes
  useLayoutEffect(() => {
    scrollToTop()
  }, [location.pathname])

  return (
    <div ref={containerRef} className="mt-10 h-[80vh]">
      {/* Mobile Navigation - Horizontal Scrollable */}
      <div className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 lg:hidden">
        <div className="px-2 py-5">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
          <div className="scrollbar-hide flex space-x-2 overflow-x-auto pb-2">
            <SettingsNavButton isActive={getCurrentTab() === "general"} onClick={() => handleTabChange("general")}>
              General
            </SettingsNavButton>
            <SettingsNavButton isActive={getCurrentTab() === "team"} onClick={() => handleTabChange("team")}>
              Team
            </SettingsNavButton>
            <SettingsNavButton isActive={getCurrentTab() === "credits"} onClick={() => handleTabChange("credits")}>
              Credits
            </SettingsNavButton>

            <SettingsNavButton isActive={getCurrentTab() === "billing"} onClick={() => handleTabChange("billing")}>
              Billing
            </SettingsNavButton>

            <SettingsNavButton isActive={getCurrentTab() === "developer"} onClick={() => handleTabChange("developer")}>
              Developer
            </SettingsNavButton>
          </div>
        </div>
      </div>

      <div className="flex h-full">
        {/* Desktop Sidebar Navigation */}
        <div className="hidden w-1/6 border-r border-gray-200 bg-white pl-0 dark:border-transparent dark:bg-transparent lg:block">
          <div className="space-y-2">
            <h2 className="mb-6 px-2 text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
            <div className="grid gap-2 px-2">
              <SettingsNavButton isActive={getCurrentTab() === "general"} onClick={() => handleTabChange("general")}>
                General
              </SettingsNavButton>

              <SettingsNavButton isActive={getCurrentTab() === "team"} onClick={() => handleTabChange("team")}>
                Team
              </SettingsNavButton>

              <SettingsNavButton isActive={getCurrentTab() === "credits"} onClick={() => handleTabChange("credits")}>
                Credits
              </SettingsNavButton>

              <SettingsNavButton isActive={getCurrentTab() === "billing"} onClick={() => handleTabChange("billing")}>
                Billing
              </SettingsNavButton>

              <SettingsNavButton
                isActive={getCurrentTab() === "developer"}
                onClick={() => handleTabChange("developer")}
              >
                Developer
              </SettingsNavButton>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="w-full py-4 lg:w-5/6">
          <div className="">
            {getCurrentTab() === "general" && <GeneralSettings />}

            {getCurrentTab() === "team" && (
              <div>
                <div className="mb-6 pl-4">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Settings</h1>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">Manage your team members and invitations.</p>
                </div>
                <TeamMembersList />
              </div>
            )}

            {getCurrentTab() === "credits" && (
              <div>
                <CreditsSettings />
              </div>
            )}

            {getCurrentTab() === "billing" && (
              <div>
                <div className="mb-6 pl-4">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing Settings</h1>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">Manage your subscription and payment methods.</p>
                </div>
                <BillingSettings />
              </div>
            )}

            {getCurrentTab() === "developer" && (
              <div>
                <div className="mb-6 pl-4">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Developer Settings</h1>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">Manage your API keys and developer tools.</p>
                </div>
                <DeveloperSettings />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
